import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Row, Button } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import axios from "axios";
import { toast } from 'react-toastify';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import Spinner from 'react-bootstrap/Spinner';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import moment from 'moment';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import { getPersistentGroup, setPersistentGroup } from 'helpers/persistence';

const COMP_KEY = 'logged_in_report';

const LoggedInReport = () => {
    const [error, setError] = useState('');
    const [loader, setLoader] = useState(false);
    const [loading, setLoading] = useState(false);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [spinner, showSpinner] = useState(false);
    const [facilityError, setFacilityError] = useState(false);
    const groupOptions = JSON.parse(localStorage.getItem("groupsData")) || [];
    const initialGroup = getPersistentGroup(COMP_KEY, groupOptions, groupOptions[0] || null);
    const [formData, setFormData] = useState({
        hcp: '',
        group: initialGroup ? initialGroup.value : '',
    });
    const [selectedGroup, setSelectedGroup] = useState(initialGroup);
    const [showResults, setShowResults] = React.useState(false)
    const [tableData, setTableData] = useState([]);
    const [lastActiveDays, setLastActiveDays] = useState(7);
    const lastActiveOptions = [
        { key: 'Last Active within 7 days', value: 7 },
        { key: 'Last Active within 15 days', value: 15 },
        { key: 'Last Active within 30 days', value: 30 },
        { key: 'Last Active within 90 days', value: 90 },
        { key: 'Inactive for more than 90 days', value: -90 }
    ];


    const columns = [
        {
            accessorKey: 'username',
            header: 'Username',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
        },
        {
            accessorKey: 'name', header: 'Full Name',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
        },
        {
            accessorKey: 'is_active', header: 'Status',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
        },
        {
            accessorKey: 'last_login_timestamp',
            header: 'Last Login',
            meta: {
                headerProps: { className: 'text-900 text-start' },
                cellProps: { className: 'text-start' }
            },
        },
    ]
    useEffect(() => {
        if (spinner) {
            setShowResults(false);
        }
    }, [spinner])

    useEffect(() => {
        fetchData()
    }, [selectedGroup, lastActiveDays]);

    const fetchData = async () => {
        setLoader(true);
        setIframeSrc(null);
        let tokenStr = localStorage.getItem("accessToken");
        let url = `${process.env.REACT_APP_SERVER_URL}` + "/reports/recent-loggedin";
        let pelmedsHcpOrderData = {}
        if (Number(localStorage.getItem("roleId")) < 4) {
            pelmedsHcpOrderData.group_id = formData.group;
        }
        if (lastActiveDays) {
            pelmedsHcpOrderData.no_of_days = lastActiveDays;
        }
        let result = [];

        try {
            const response = await axios.post(url, pelmedsHcpOrderData, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
            if (response.data.status) {
                let objData = response.data.data.recentUsers;
                objData.map((item) => {
                    let status = item.is_active ? 'Active' : 'Inactive';
                    let lastLogin = item.last_login_timestamp
                        ? moment(item.last_login_timestamp)
                            .tz("America/New_York")
                            .format("MMMM D, YYYY [at] h:mm A")
                        : '';

                    result.push({
                        username: item.username,
                        name: item.first_name + ' ' + item.last_name,
                        is_active: status,
                        last_login_timestamp: lastLogin
                    });
                    // return null;
                });
                setTableData(result);
            }
        } catch (err) {
            setLoader(false);
        } finally {
            setLoader(false);
        }
    }

    const handleSubmit = async e => {
        e.preventDefault();
        setLoading(true);
        showSpinner(true);

        let tokenStr = localStorage.getItem("accessToken");
        let pelmedsHcpOrderUrl = `${process.env.REACT_APP_SERVER_URL}` + "/reports/recent-loggedin-report";
        let pelmedsHcpOrderData = {}
        if (Number(localStorage.getItem("roleId")) < 4) {
            pelmedsHcpOrderData.group_id = formData.group;
        }
        if (lastActiveDays) {
            pelmedsHcpOrderData.no_of_days = lastActiveDays;
        }

        showSpinner(false);

        try {
            const response = await axios.post(pelmedsHcpOrderUrl, pelmedsHcpOrderData, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
            if (!(response.data.status)) {
                setError(response.data.message);
            }
            const blob = new Blob([response.data], { type: "text/html" });
            const blobUrl = URL.createObjectURL(blob);
            setIframeSrc(blobUrl);
        } catch (err) {
            setError("Failed to print content.");
        } finally {
            setLoading(false);
        }
    }


    const handleFieldChange = e => {
        setSelectedGroup(e);
        setPersistentGroup(COMP_KEY, e);
        setFormData({
            ...formData,
            "group": e?.value ?? ''
        });
    };

    const handleHcpChange = e => {
        setFormData({
            ...formData,
            'hcp': e.value
        });
    };

    const breadcrumbItems = [
        { name: 'Dashboard', href: '/main', isActive: false },
        { name: "Users Activity Report", href: '', isActive: true }
    ];

    const table = useAdvanceTable({
        data: tableData,
        columns,
        selection: false,
        sortable: false,
        pagination: true
    });

    return (
        <>
            <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
            <Card className="mb-3">
                <Card.Header as="h6" className="bg-body-tertiary">
                    Users Activity Report
                </Card.Header>
                <Card.Body className="p-3">
                    <Row>
                        <Col xs="4" className="mt-0">
                            <Form.Group>
                                <Form.Label>Last Active Filter</Form.Label>
                                <ThemedSelect
                                    name="lastactive"
                                    placeholder="Select Last Active Range"
                                    classNamePrefix="react-select"
                                    options={lastActiveOptions}
                                    value={lastActiveOptions.find(option => option.value === lastActiveDays)}
                                    getOptionLabel={option => option.key}
                                    getOptionValue={option => option.value}
                                    onChange={(selected) => setLastActiveDays(selected.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs="4">
                            {Number(localStorage.getItem("roleId")) < 4 && (
                                <Form.Group>
                                    <Form.Label className="required">Group</Form.Label>
                                    <ThemedSelect
                                        name="facilityname"
                                        closeMenuOnSelect={true}
                                        placeholder="Select Group"
                                        classNamePrefix="react-select"
                                        options={groupOptions}
                                        value={selectedGroup}
                                        getOptionLabel={option => option.key}
                                        getOptionValue={option => option.value}
                                        onChange={handleFieldChange}
                                        styles={{
                                            control: provided => ({
                                                ...provided,
                                                borderColor: "#ced4da",
                                                boxShadow: "none",
                                                "&:hover": { borderColor: "#80bdff" }
                                            }),
                                            option: (provided, { isFocused, isSelected }) => ({
                                                ...provided,
                                                backgroundColor: isSelected
                                                    ? "#e9ecef !important"
                                                    : isFocused
                                                        ? "#f8f9fa !important"
                                                        : "white !important",
                                                color: "#212529 !important",
                                                cursor: "pointer"
                                            }),
                                            singleValue: provided => ({
                                                ...provided,
                                                color: "#212529"
                                            })
                                        }}
                                    />
                                    {facilityError && (
                                        <Form.Text className="text-danger">
                                            Please select a group.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            )}
                        </Col>
                        <Col xs="4" className='view-patient-search-holder'>
                            <AdvanceTableProvider {...table}>
                                {/* <div className="d-flex justify-content-end mb-3">
                                    <div className="w-25">
                                    </div>
                                </div> */}
                                        <AdvanceTableSearchBox placeholder="Search..." />
                            </AdvanceTableProvider>
                        </Col>
                    </Row>

                    {loader ? <ShimmerUI></ShimmerUI> : tableData.length === 0 ? <div className="text-center p-5">No data available.</div> :
                        <>
                            <Row className="mt-3">
                                <Col>
                                    <AdvanceTableProvider {...table}>
                                        <AdvanceTable
                                            headerClassName="bg-200 text-nowrap align-middle text-center"
                                            rowClassName="text-nowrap align-middle text-center"
                                            tableProps={{
                                                size: 'sm',
                                                striped: true,
                                                className: 'fs-10 mb-0 overflow-hidden',
                                            }}
                                        />

                                        {/* Footer */}
                                        <div className="mt-3 d-flex justify-content-between align-items-center">
                                            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                                        </div>
                                    </AdvanceTableProvider>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12" className="mb-1 mt-1">
                                    <div style={{
                                        height: 0,
                                        overflow: 'hidden'
                                    }}>
                                        {iframeSrc && (
                                            <iframe
                                                src={iframeSrc}
                                                style={{ width: "100%", height: "2000px", border: "none" }}
                                                title="Preview Content"
                                            />
                                        )}
                                    </div>

                                    <div className="text-center px-3 mt-3">
                                        {!loading ? <Button className="btn btn-primary" onClick={handleSubmit}>
                                            Generate
                                        </Button> : <Spinner animation="border" role="status" size="sm"></Spinner>}
                                    </div>

                                </Col>
                            </Row>
                        </>
                    }
                </Card.Body>
            </Card>

            <div>
                <br />
                {showResults && (
                    <a href={hcporderrpt} target="_blank" rel="noreferrer">
                        <button className="btn btn-primary">
                            📄 Download Report
                        </button>
                    </a>
                )}

            </div>
        </>
    );
};

LoggedInReport.propTypes = {
    layout: PropTypes.string,
    hasLabel: PropTypes.bool
};

LoggedInReport.defaultProps = {
    layout: 'simple',
    hasLabel: false
};

export default LoggedInReport;
